import type { PricePoint } from '../types';

export type MatrixDimKey = 'length_m' | 'width_m' | 'projection_m';

export type MatrixLayout =
  | { mode: 'none' }
  | { mode: '1d'; key: MatrixDimKey; values: number[] }
  | {
      mode: '2d';
      primary: MatrixDimKey;
      secondary: MatrixDimKey;
      primaryValues: number[];
    };

const DIM_EPS = 1e-6;

export function dimEquals(a: number, b: number): boolean {
  return Math.abs(a - b) < DIM_EPS;
}

function uniqueSorted(values: number[]): number[] {
  const seen = new Set<string>();
  const out: number[] = [];
  for (const v of values) {
    if (!Number.isFinite(v) || v <= 0) continue;
    const key = v.toFixed(4);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(v);
  }
  return out.sort((a, b) => a - b);
}

function columnHas(matrix: PricePoint[], key: MatrixDimKey): boolean {
  return matrix.some((r) => r[key] != null && Number.isFinite(Number(r[key])));
}

export function analyzePriceMatrix(matrix: PricePoint[]): MatrixLayout {
  if (!matrix.length) return { mode: 'none' };

  const hasWidth = columnHas(matrix, 'width_m');
  const hasProj = columnHas(matrix, 'projection_m');
  const hasLen = columnHas(matrix, 'length_m');

  if (hasWidth && hasProj) {
    return {
      mode: '2d',
      primary: 'width_m',
      secondary: 'projection_m',
      primaryValues: uniqueSorted(
        matrix.map((r) => r.width_m).filter((v): v is number => v != null && Number.isFinite(v)),
      ),
    };
  }

  if (hasWidth && hasLen && !hasProj) {
    return {
      mode: '2d',
      primary: 'width_m',
      secondary: 'length_m',
      primaryValues: uniqueSorted(
        matrix.map((r) => r.width_m).filter((v): v is number => v != null && Number.isFinite(v)),
      ),
    };
  }

  if (hasLen && !hasWidth && !hasProj) {
    return {
      mode: '1d',
      key: 'length_m',
      values: uniqueSorted(
        matrix.map((r) => r.length_m).filter((v): v is number => v != null && Number.isFinite(v)),
      ),
    };
  }

  if (hasWidth && !hasProj && !hasLen) {
    return {
      mode: '1d',
      key: 'width_m',
      values: uniqueSorted(
        matrix.map((r) => r.width_m).filter((v): v is number => v != null && Number.isFinite(v)),
      ),
    };
  }

  if (hasProj && !hasWidth && !hasLen) {
    return {
      mode: '1d',
      key: 'projection_m',
      values: uniqueSorted(
        matrix.map((r) => r.projection_m).filter((v): v is number => v != null && Number.isFinite(v)),
      ),
    };
  }

  return { mode: 'none' };
}

export function secondaryValuesForPrimary(
  matrix: PricePoint[],
  primary: MatrixDimKey,
  secondary: MatrixDimKey,
  primaryValue: number,
): number[] {
  return uniqueSorted(
    matrix
      .filter(
        (r) =>
          r[primary] != null &&
          dimEquals(Number(r[primary]), primaryValue) &&
          r[secondary] != null &&
          Number.isFinite(Number(r[secondary])) &&
          Number(r.price) > 0,
      )
      .map((r) => Number(r[secondary])),
  );
}

export function matrixPointPrice(
  matrix: PricePoint[],
  primary: MatrixDimKey,
  secondary: MatrixDimKey | null,
  primaryValue: number,
  secondaryValue: number | null,
): number | null {
  if (secondary && secondaryValue != null) {
    const row = matrix.find(
      (r) =>
        r[primary] != null &&
        dimEquals(Number(r[primary]), primaryValue) &&
        r[secondary] != null &&
        dimEquals(Number(r[secondary]), secondaryValue),
    );
    return row?.price ?? null;
  }
  const row = matrix.find((r) => r[primary] != null && dimEquals(Number(r[primary]), primaryValue));
  return row?.price ?? null;
}

export function roundUpOnGrid(value: number, grid: number[]): number | null {
  if (!grid.length || !Number.isFinite(value) || value <= 0) return null;
  return grid.find((d) => d >= value) ?? grid[grid.length - 1] ?? null;
}

const DEFAULT_DIM_LABELS: Record<MatrixDimKey, string> = {
  length_m: 'Длина карниза',
  width_m: 'Ширина',
  projection_m: 'Вынос',
};

/** Подписи осей: админ `dim_label` / `dim_secondary_label` или эвристика по категории. */
export function resolveMatrixDimLabels(args: {
  layout: MatrixLayout;
  schema?: Record<string, unknown> | null;
  category?: string | null;
  branchSlug?: string | null;
}): { primary: string; secondary: string } {
  const schema = args.schema ?? {};
  const hay = `${args.category ?? ''} ${args.branchSlug ?? ''}`.toLowerCase();

  if (args.layout.mode === '2d') {
    const primaryDefault =
      args.layout.primary === 'width_m'
        ? 'Ширина'
        : DEFAULT_DIM_LABELS[args.layout.primary];
    let secondaryDefault = 'Вынос';
    if (hay.includes('zip')) secondaryDefault = 'Высота';
    else if (hay.includes('фасад')) secondaryDefault = 'Вылет ткани';
    else if (args.layout.secondary === 'length_m') secondaryDefault = 'Длина';

    const primary =
      typeof schema.dim_label === 'string' && schema.dim_label.trim()
        ? schema.dim_label.trim()
        : primaryDefault;
    const secondary =
      typeof schema.dim_secondary_label === 'string' && schema.dim_secondary_label.trim()
        ? schema.dim_secondary_label.trim()
        : secondaryDefault;
    return { primary, secondary };
  }

  if (args.layout.mode === '1d') {
    let fallback = DEFAULT_DIM_LABELS[args.layout.key];
    if (args.layout.key === 'length_m' && (hay.includes('pergol') || hay.includes('пергол'))) {
      fallback = 'Длина, м';
    }
    const label =
      typeof schema.dim_label === 'string' && schema.dim_label.trim()
        ? schema.dim_label.trim()
        : fallback;
    return { primary: label, secondary: '' };
  }

  return { primary: 'Размер', secondary: '' };
}

export function sizeSelectionComplete(
  layout: MatrixLayout,
  primary: number | null,
  secondary: number | null,
): boolean {
  if (layout.mode === 'none') return true;
  if (layout.mode === '1d') return layout.values.length === 0 || primary !== null;
  return primary !== null && secondary !== null;
}

export type DimensionQuoteStatus = 'exact' | 'rounded' | 'price_on_request' | 'pending';

export type DimensionQuote = {
  status: DimensionQuoteStatus;
  priceOnRequest: boolean;
  /** Можно вызвать price API (есть ячейка прайса после округления). */
  canPrice: boolean;
  /** Клиент указал все нужные размеры — можно в корзину. */
  canAddToCart: boolean;
  billedPrimary: number | null;
  billedSecondary: number | null;
  price: number | null;
  messages: string[];
  maxPrimary: number | null;
  maxSecondary: number | null;
};

function maxOnGrid(grid: number[]): number | null {
  return grid.length > 0 ? grid[grid.length - 1]! : null;
}

/** Калькулятор размеров: округление вверх по сетке, «цена по запросу», тексты для клиента. */
export function evaluateMatrixDimensions(
  layout: MatrixLayout,
  matrix: PricePoint[],
  primary: number | null,
  secondary: number | null,
  labels: { primary: string; secondary: string },
): DimensionQuote {
  const base: DimensionQuote = {
    status: 'pending',
    priceOnRequest: false,
    canPrice: false,
    canAddToCart: false,
    billedPrimary: null,
    billedSecondary: null,
    price: null,
    messages: [],
    maxPrimary: null,
    maxSecondary: null,
  };

  if (layout.mode === 'none') {
    return { ...base, status: 'exact', canPrice: true, canAddToCart: true };
  }

  if (layout.mode === '1d') {
    const grid = layout.values;
    base.maxPrimary = maxOnGrid(grid);
    if (primary == null || !Number.isFinite(primary) || primary <= 0) {
      return base;
    }
    base.canAddToCart = true;
    if (base.maxPrimary != null && primary > base.maxPrimary + DIM_EPS) {
      return {
        ...base,
        status: 'price_on_request',
        priceOnRequest: true,
        messages: [
          `${labels.primary} ${primary} м выше прайс-листа (макс. ${base.maxPrimary} м). Оформите заказ — менеджер уточнит цену по телефону.`,
        ],
      };
    }
    const billed = roundUpOnGrid(primary, grid);
    if (billed == null) {
      return {
        ...base,
        status: 'price_on_request',
        priceOnRequest: true,
        messages: ['Не удалось подобрать размер по прайсу. Менеджер свяжется с вами.'],
      };
    }
    const price = matrixPointPrice(matrix, layout.key, null, billed, null);
    const exact = dimEquals(primary, billed);
    const messages: string[] = [];
    if (!exact) {
      messages.push(
        `В прайсе учтено ${billed} м (ваш замер ${primary} м, округление вверх до ячейки сетки).`,
      );
    }
    if (price == null) {
      return {
        ...base,
        status: 'price_on_request',
        priceOnRequest: true,
        billedPrimary: billed,
        messages: [
          ...messages,
          'Для этого размера нет готовой ячейки в прайсе. Заказ можно оформить — финальную стоимость согласует менеджер.',
        ],
      };
    }
    return {
      ...base,
      status: exact ? 'exact' : 'rounded',
      canPrice: true,
      billedPrimary: billed,
      price,
      messages,
    };
  }

  const gridP = layout.primaryValues;
  base.maxPrimary = maxOnGrid(gridP);
  if (primary == null || !Number.isFinite(primary) || primary <= 0) {
    return base;
  }
  if (secondary == null || !Number.isFinite(secondary) || secondary <= 0) {
    return { ...base, canAddToCart: false };
  }
  base.canAddToCart = true;

  if (base.maxPrimary != null && primary > base.maxPrimary + DIM_EPS) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      messages: [
        `${labels.primary} ${primary} м выше прайс-листа (макс. ${base.maxPrimary} м). Оформите заказ — менеджер подберёт решение и цену.`,
      ],
    };
  }

  const billedP = roundUpOnGrid(primary, gridP);
  if (billedP == null) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      messages: ['Не удалось подобрать ширину по прайсу. Менеджер уточнит параметры.'],
    };
  }

  const secGrid = secondaryValuesForPrimary(matrix, layout.primary, layout.secondary, billedP);
  base.maxSecondary = maxOnGrid(secGrid);

  if (!secGrid.length) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      billedPrimary: billedP,
      messages: [
        `Для ${labels.primary.toLowerCase()} ${billedP} м в прайсе нет вариантов ${labels.secondary.toLowerCase()}. Оформите заказ — менеджер согласует размер.`,
      ],
    };
  }

  if (base.maxSecondary != null && secondary > base.maxSecondary + DIM_EPS) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      billedPrimary: billedP,
      messages: [
        `${labels.secondary} ${secondary} м выше прайс-листа для ${billedP} м (макс. ${base.maxSecondary} м). Менеджер уточнит возможность и цену.`,
      ],
    };
  }

  const billedS = roundUpOnGrid(secondary, secGrid);
  if (billedS == null) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      billedPrimary: billedP,
      messages: ['Не удалось подобрать второй размер по прайсу.'],
    };
  }

  const price = matrixPointPrice(matrix, layout.primary, layout.secondary, billedP, billedS);
  const exactP = dimEquals(primary, billedP);
  const exactS = dimEquals(secondary, billedS);
  const messages: string[] = [];
  if (!exactP || !exactS) {
    messages.push(
      `В прайсе учтено ${billedP}×${billedS} м (ваш замер ${primary}×${secondary} м, округление вверх по сетке).`,
    );
  }

  if (price == null) {
    return {
      ...base,
      status: 'price_on_request',
      priceOnRequest: true,
      billedPrimary: billedP,
      billedSecondary: billedS,
      messages: [
        ...messages,
        'Комбинация размеров вне готовой сетки прайса. Заказ можно оформить — менеджер свяжется для уточнения.',
      ],
    };
  }

  return {
    ...base,
    status: exactP && exactS ? 'exact' : 'rounded',
    canPrice: true,
    billedPrimary: billedP,
    billedSecondary: billedS,
    price,
    messages,
  };
}
